// src/components/kanban/BoardFiltersBar.tsx
/**
 * Barra filtri della Kanban board: persona, richiesta, periodo (su previsto/creazione/
 * completamento), più le preferenze di visualizzazione (completate, copertine) e i
 * badge riassuntivi di metadata quando disponibili.
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Chip, Group, Paper, SegmentedControl, Switch } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { KanbanBoardFilters, KanbanBoardMetadata, KanbanDateField } from '../../types/api';
import { parseDateOnly, toDateOnlyString } from '../../utils/date-utils';
import UserMultiSelect from './UserMultiSelect';
import RequestLookupSelect from './RequestLookupSelect';

interface BoardFiltersBarProps {
  filters: KanbanBoardFilters;
  onFiltersChange: (partial: Partial<KanbanBoardFilters>) => void;
  onReset: () => void;
  isOnlyMine: boolean;
  onToggleOnlyMine: () => void;
  showCovers: boolean;
  onShowCoversChange: (v: boolean) => void;
  showCompleted: boolean;
  onShowCompletedChange: (v: boolean) => void;
  metadata?: KanbanBoardMetadata;
}

const DATE_FIELD_OPTIONS: { label: string; value: KanbanDateField }[] = [
  { label: 'Prevista', value: 'planned' },
  { label: 'Creazione', value: 'created' },
  { label: 'Completamento', value: 'completed' },
];

export default function BoardFiltersBar({
  filters,
  onFiltersChange,
  onReset,
  isOnlyMine,
  onToggleOnlyMine,
  showCovers,
  onShowCoversChange,
  showCompleted,
  onShowCompletedChange,
  metadata,
}: BoardFiltersBarProps) {
  // Stato locale per il range date: DatePickerInput invoca onChange con [start, null] dopo
  // il primo click. Si propaga ai filtri/URL solo quando entrambe le date sono impostate
  // o entrambe azzerate (altrimenti si aspetta il secondo click).
  const [range, setRange] = useState<[Date | null, Date | null]>([
    parseDateOnly(filters.from),
    parseDateOnly(filters.to),
  ]);

  useEffect(() => {
    setRange([parseDateOnly(filters.from), parseDateOnly(filters.to)]);
  }, [filters.from, filters.to]);

  const handleRangeChange = (value: [Date | null, Date | null]) => {
    setRange(value);
    const [start, end] = value;
    if (start && end) {
      onFiltersChange({ from: toDateOnlyString(start), to: toDateOnlyString(end) });
    } else if (!start && !end) {
      onFiltersChange({ from: undefined, to: undefined });
    }
  };

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <UserMultiSelect
            value={filters.assigneeIds ?? []}
            onChange={(ids) => onFiltersChange({ assigneeIds: ids.length ? ids : undefined })}
            label="Persone"
            placeholder="Filtra per persona"
          />
          <Chip checked={isOnlyMine} onChange={onToggleOnlyMine}>
            Solo i miei
          </Chip>

          <RequestLookupSelect
            value={filters.requestId}
            onChange={(id) => onFiltersChange({ requestId: id })}
            withTasksOnly
            label="Richiesta"
            placeholder="Filtra per richiesta"
          />

          <SegmentedControl
            value={filters.dateField ?? 'planned'}
            onChange={(v) => onFiltersChange({ dateField: v as KanbanDateField })}
            data={DATE_FIELD_OPTIONS}
          />

          <DatePickerInput
            type="range"
            locale="it"
            valueFormat="DD/MM/YYYY"
            value={range}
            onChange={handleRangeChange}
            clearable
            placeholder="Periodo"
          />

          <Button variant="subtle" onClick={onReset}>
            Azzera filtri
          </Button>
        </Group>

        <Group gap="sm" wrap="wrap">
          <Switch
            label="Mostra completate"
            checked={showCompleted}
            onChange={(e) => onShowCompletedChange(e.currentTarget.checked)}
          />
          <Switch
            label="Mostra copertine"
            checked={showCovers}
            onChange={(e) => onShowCoversChange(e.currentTarget.checked)}
          />
        </Group>
      </Group>

      {metadata && (
        <Group gap="xs" mt="sm">
          <Badge variant="light" color="blue">
            Totale: {metadata.totalTasks}
          </Badge>
          <Badge variant="light" color={metadata.overdueTasks > 0 ? 'red' : 'gray'}>
            In ritardo: {metadata.overdueTasks}
          </Badge>
          {metadata.completedOnTime != null && (
            <Badge variant="light" color="green">
              Completati in tempo: {metadata.completedOnTime}
            </Badge>
          )}
          {metadata.completedLate != null && (
            <Badge variant="light" color={metadata.completedLate > 0 ? 'red' : 'gray'}>
              Completati in ritardo: {metadata.completedLate}
            </Badge>
          )}
        </Group>
      )}
    </Paper>
  );
}
